import React, { useState, useEffect, useRef, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import './App.css';

// A4 méret (px, 96 DPI): 794 x 1123
const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

// Pure Procrastination Scale (PPS) kérdések - Steel (2010)
const QUESTIONS = [
    { id: 1, text: "Halogatom a döntéshozatalt, amíg túl késő nem lesz.", reverse: false },
    { id: 2, text: "Még ha meghozom is a döntést, késlekedem a cselekvéssel.", reverse: false },
    { id: 3, text: "Sok időt pazarlok lényegtelen dolgokkal, mielőtt a végső döntéshez érnék.", reverse: false },
    { id: 4, text: "Amikor valamilyen határidőre készülök, gyakran pazarlom az időt más dolgokkal.", reverse: false },
    { id: 5, text: "Általában halogatok, mielőtt hozzákezdek a munkához, amit el kell végeznem.", reverse: false },
    { id: 6, text: "Folyamatosan azt mondom, hogy 'holnap megcsinálom'.", reverse: false },
    { id: 7, text: "Gyakran találom magam olyan feladatok elvégzésében, amelyeket napokkal korábban terveztem elvégezni.", reverse: false },
    { id: 8, text: "Általában a lehető leggyorsabban meghozom a döntéseimet.", reverse: true },
    { id: 9, text: "Gyakran kifutok az időből.", reverse: false },
    { id: 10, text: "Semleges állapotban ragadok, annak ellenére, hogy tudom, mennyire fontos lenne elkezdeni.", reverse: false },
    { id: 11, text: "Még azokat a feladatokat is, amelyek nem igényelnek mást, csak leülni és elvégezni őket, ritkán végzem el napokig.", reverse: false },
    { id: 12, text: "Halogatom a döntések meghozatalát.", reverse: false }
];

function App() {
    const [shuffledQuestions, setShuffledQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [showResult, setShowResult] = useState(false);
    const [result, setResult] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const resultRef = useRef(null);
    const touchStartXRef = useRef(0);

    const toggleFullscreen = () => {
        if (window !== window.top) {
            window.parent.postMessage({ type: 'halogatas-teszt-fullscreen' }, '*');
            return;
        }
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            document.documentElement.requestFullscreen();
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Kérdések randomizálása komponens betöltésekor
    useEffect(() => {
        const shuffled = [...QUESTIONS].sort(() => Math.random() - 0.5);
        setShuffledQuestions(shuffled);
    }, []);

    // LearnDash iframe automatikus méretbeállítás
    useEffect(() => {
        // Ha iframe-ben vagyunk, automatikusan beállítjuk a méretet
        const isInIframe = window.self !== window.top;
        
        if (isInIframe) {
            // PostMessage küldése a szülő ablaknak a méretről
            const sendHeight = () => {
                const height = document.documentElement.scrollHeight;
                window.parent.postMessage({ type: 'resize', height }, '*');
                window.parent.postMessage({ height }, '*'); // kompatibilitás: e.data.height
            };

            // Méret küldése betöltéskor és változásokkor
            sendHeight();
            window.addEventListener('resize', sendHeight);
            
            // MutationObserver a dinamikus tartalom változásainak figyelésére
            const observer = new MutationObserver(sendHeight);
            observer.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style', 'class']
            });

            // Periodikus frissítés is (biztonsági net)
            const interval = setInterval(sendHeight, 1000);

            return () => {
                window.removeEventListener('resize', sendHeight);
                observer.disconnect();
                clearInterval(interval);
            };
        }
    }, [showResult, currentQuestionIndex, answers]);

    const nextQuestion = useCallback(() => {
        setCurrentQuestionIndex(prev => {
            if (prev < shuffledQuestions.length - 1) {
                return prev + 1;
            }
            return prev;
        });
    }, [shuffledQuestions.length]);

    const previousQuestion = useCallback(() => {
        setCurrentQuestionIndex(prev => {
            if (prev > 0) {
                return prev - 1;
            }
            return prev;
        });
    }, []);

    const handleAnswerChange = useCallback((questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
        
        // Automatikus továbblépés
        setTimeout(() => {
            setCurrentQuestionIndex(prev => {
                if (prev < shuffledQuestions.length - 1) {
                    return prev + 1;
                }
                return prev;
            });
        }, 300);
    }, [shuffledQuestions.length]);

    // Billentyűzet navigáció
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (showResult) return;

            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                nextQuestion();
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                previousQuestion();
            } else if (e.key >= '1' && e.key <= '5') {
                const questionId = shuffledQuestions[currentQuestionIndex]?.id;
                if (questionId) {
                    handleAnswerChange(questionId, parseInt(e.key));
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentQuestionIndex, shuffledQuestions, showResult, nextQuestion, previousQuestion, handleAnswerChange]);

    const handleTouchStart = (e) => {
        touchStartXRef.current = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
        const endX = e.changedTouches[0].screenX;
        const startX = touchStartXRef.current;
        
        // Swipe kezelés közvetlenül itt
        const swipeThreshold = 50;
        const diff = startX - endX;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0) {
                nextQuestion();
            } else {
                previousQuestion();
            }
        }
    };

    const getAnsweredCount = () => {
        return Object.keys(answers).length;
    };

    const getProgress = () => {
        return (getAnsweredCount() / QUESTIONS.length) * 100;
    };

    const evaluateTest = () => {
        let totalScore = 0;
        let answeredCount = 0;

        shuffledQuestions.forEach(q => {
            const answer = answers[q.id];
            if (answer !== undefined) {
                answeredCount++;
                let score = parseInt(answer);
                
                // Reverse scoring for item 8
                if (q.reverse) {
                    score = 6 - score;
                }
                
                totalScore += score;
            }
        });

        if (answeredCount < QUESTIONS.length) {
            alert('❗ Kérjük, válaszoljon minden kérdésre a kiértékelés előtt!');
            return;
        }

        // Átlagpontszám számítása (1-5 skálán)
        const averageScore = totalScore / QUESTIONS.length;

        let interpretation = '';
        let emoji = '';
        let category = '';
        
        if (averageScore < 2.0) {
            emoji = '🟢';
            category = 'Alacsony halogatási hajlam';
            interpretation = `
                <h3>${emoji} ${category} (${averageScore.toFixed(2)} átlag)</h3>
                <p>Gratulálunk! Az Ön halogatási hajlama az átlagosnál <strong>lényegesen alacsonyabb</strong>. Jellemzően időben elkezdi a feladatokat, és nem halogatja a fontos döntéseket.</p>
                <p><strong>Erősségei:</strong></p>
                <ul>
                    <li>✓ Jó időgazdálkodás</li>
                    <li>✓ Hatékony döntéshozatal</li>
                    <li>✓ Proaktív hozzáállás</li>
                </ul>
                <p><strong>Tipp:</strong> Ossza meg stratégiáit másokkal, akik küzdenek a halogatással!</p>
            `;
        } else if (averageScore >= 2.0 && averageScore < 2.5) {
            emoji = '🟡';
            category = 'Alacsony-közepes halogatási hajlam';
            interpretation = `
                <h3>${emoji} ${category} (${averageScore.toFixed(2)} átlag)</h3>
                <p>Az Ön halogatási hajlama az <strong>átlag alatt van</strong>. Általában jól kezeli a feladatokat és határidőket, bár időnként előfordulhat némi késlekedés.</p>
                <p><strong>Jellemzők:</strong></p>
                <ul>
                    <li>✓ Többnyire időben elkezdett feladatok</li>
                    <li>⚠️ Alkalmanként előforduló halogatás</li>
                    <li>✓ Általában jó teljesítmény</li>
                </ul>
                <p><strong>Tipp:</strong> Azonosítsa azokat a helyzeteket, amikor hajlamos halogatni, és dolgozzon ezeken!</p>
            `;
        } else if (averageScore >= 2.5 && averageScore < 3.0) {
            emoji = '🟠';
            category = 'Közepes halogatási hajlam';
            interpretation = `
                <h3>${emoji} ${category} (${averageScore.toFixed(2)} átlag)</h3>
                <p>Az Ön halogatási hajlama <strong>átlagos szinten</strong> van. Vannak területek, ahol hatékonyan dolgozik, de mások, ahol hajlamos késlekedni.</p>
                <p><strong>Megfigyelések:</strong></p>
                <ul>
                    <li>⚠️ Változó teljesítmény a különböző feladatoknál</li>
                    <li>⚠️ Gyakori "holnapra halasztás"</li>
                    <li>⚠️ Időnként kifutás az időből</li>
                </ul>
                <p><strong>Javaslatok:</strong></p>
                <ul>
                    <li>📅 Használjon naptárat és emlékeztetőket</li>
                    <li>🎯 Ossza kisebb részekre a nagy feladatokat</li>
                    <li>⏰ Állítson be szigorúbb határidőket magának</li>
                </ul>
            `;
        } else if (averageScore >= 3.0 && averageScore < 4.0) {
            emoji = '🔴';
            category = 'Magas halogatási hajlam';
            interpretation = `
                <h3>${emoji} ${category} (${averageScore.toFixed(2)} átlag)</h3>
                <p>Az Ön halogatási hajlama az <strong>átlagosnál magasabb</strong>. Ez valószínűleg befolyásolja a termelékenységét, és stresszt okozhat az életében.</p>
                <p><strong>Gyakori problémák:</strong></p>
                <ul>
                    <li>⚠️ Rendszeres késlekedés a feladatok elkezdésével</li>
                    <li>⚠️ Döntések halogatása</li>
                    <li>⚠️ Időpazarlás lényegtelen dolgokkal</li>
                    <li>⚠️ Határidők elmulasztása</li>
                </ul>
                <p><strong>Ajánlott lépések:</strong></p>
                <ul>
                    <li>📚 Tanuljon időgazdálkodási technikákat (Pomodoro, Getting Things Done)</li>
                    <li>🎯 Használja a "2 perces szabályt" - ha 2 percnél kevesebb, csináld meg most!</li>
                    <li>👥 Kérjen külső elszámoltathatóságot (accountability partner)</li>
                    <li>🧠 Dolgozzon az önfegyelmen és motiváción</li>
                </ul>
            `;
        } else {
            emoji = '🆘';
            category = 'Nagyon magas halogatási hajlam';
            interpretation = `
                <h3>${emoji} ${category} (${averageScore.toFixed(2)} átlag)</h3>
                <p>Az Ön halogatási hajlama <strong>jelentősen az átlag felett</strong> van. Ez komolyan befolyásolhatja az életminőségét, karrierjét és kapcsolatait.</p>
                <p><strong>Súlyos problémák:</strong></p>
                <ul>
                    <li>❌ Krónikus halogatás</li>
                    <li>❌ Súlyos időgazdálkodási nehézségek</li>
                    <li>❌ Döntésképtelenség</li>
                    <li>❌ Jelentős stressz és szorongás</li>
                </ul>
                <p><strong>Ajánlott intézkedések:</strong></p>
                <ul>
                    <li>🏥 <strong>Fontolja meg szakmai segítség kérését</strong> (pszichológus, coach)</li>
                    <li>📖 Olvassa el: "The Procrastination Equation" - Piers Steel</li>
                    <li>🎯 Kezdje kis lépésekkel - ne várjon a tökéletes pillanatra</li>
                    <li>🧘 Vizsgálja meg a mögöttes okokat (félelem, perfekcionizmus, alacsony önértékelés)</li>
                    <li>💊 Kizárható-e ADHD vagy egyéb figyelemzavar?</li>
                </ul>
                <p style="color: #d32f2f; font-weight: bold; margin-top: 15px;">
                    ⚠️ Ne feledje: A halogatás gyakran mögöttes pszichológiai problémák tünete lehet. Szakember segíthet azonosítani és kezelni ezeket.
                </p>
            `;
        }

        setResult({
            totalScore,
            averageScore,
            interpretation,
            emoji,
            category
        });
        setShowResult(true);
    };

    const saveAsPdf = async () => {
        if (!showResult || !result || !resultRef.current) return;
        try {
            const dateStr = new Date().toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });
            const temp = document.createElement('div');
            temp.style.cssText = 'position:fixed;left:-9999px;top:0;width:' + A4_WIDTH + 'px;background:#fff;padding:24px;padding-bottom:40px;box-sizing:border-box;font-family:Segoe UI,Tahoma,Geneva,Verdana,sans-serif;';
            temp.innerHTML =
                '<div style="text-align:center;margin-bottom:20px;font-size:1.8em;font-weight:bold;color:#1a5c47;">⏱️ Halogatás teszt</div>' +
                '<div class="result-box show" style="opacity:1;visibility:visible;display:block;transform:none;animation:none;">' +
                resultRef.current.innerHTML +
                '</div>' +
                '<div style="text-align:center;margin-top:24px;padding-top:12px;border-top:1px solid #ddd;font-size:0.9em;color:#888;">' + dateStr + '</div>';
            document.body.appendChild(temp);

            const scale = 2;
            const canvas = await html2canvas(temp, {
                scale,
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true,
                allowTaint: true,
                width: A4_WIDTH,
                windowWidth: A4_WIDTH,
                onclone: (_, el) => {
                    const box = el.querySelector('.result-box');
                    if (box) {
                        box.style.setProperty('opacity', '1', 'important');
                        box.style.setProperty('visibility', 'visible', 'important');
                        box.style.setProperty('display', 'block', 'important');
                        box.style.setProperty('transform', 'none', 'important');
                        box.style.setProperty('animation', 'none', 'important');
                    }
                }
            });
            temp.remove();

            const w = canvas.width;
            const h = canvas.height;
            const out = document.createElement('canvas');
            out.width = A4_WIDTH * scale;
            out.height = A4_HEIGHT * scale;
            const ctx = out.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, out.width, out.height);
            const scaleFit = Math.min(out.width / w, out.height / h);
            const dx = (out.width - w * scaleFit) / 2;
            const dy = (out.height - h * scaleFit) / 2;
            ctx.drawImage(canvas, 0, 0, w, h, dx, dy, w * scaleFit, h * scaleFit);

            const imgData = out.toDataURL('image/jpeg', 0.92);
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth();
            const pageH = doc.internal.pageSize.getHeight();
            doc.addImage(imgData, 'JPEG', 0, 0, pageW, pageH);
            doc.save('halogatas_teszt_eredmeny.pdf');
        } catch (error) {
            console.error('Hiba a PDF mentése során:', error);
            alert('Hiba történt a PDF mentése során: ' + error.message);
        }
    };

    const resetForm = () => {
        if (window.confirm('Biztosan újra szeretne kezdeni? Az összes válasz törlődik.')) {
            setAnswers({});
            setShowResult(false);
            setResult(null);
            setCurrentQuestionIndex(0);
            // Újra randomizáljuk a kérdéseket
            const shuffled = [...QUESTIONS].sort(() => Math.random() - 0.5);
            setShuffledQuestions(shuffled);
        }
    };

    const currentQuestion = shuffledQuestions[currentQuestionIndex];
    const answeredCount = getAnsweredCount();
    const progress = getProgress();

    const getEmojiForValue = (value) => {
        switch(value) {
            case 1: return '❌';
            case 2: return '😕';
            case 3: return '😐';
            case 4: return '✓';
            case 5: return '✓✓';
            default: return '';
        }
    };

    const getLabelForValue = (value) => {
        switch(value) {
            case 1: return 'Egyáltalán nem';
            case 2: return 'Ritkán jellemző';
            case 3: return 'Néha jellemző';
            case 4: return 'Gyakran jellemző';
            case 5: return 'Nagyon jellemző';
            default: return '';
        }
    };

    const isQuestionAnswered = (questionId) => {
        return answers[questionId] !== undefined;
    };

    return (
        <>
        <div className="container">
            <h1>⏱️ Halogatás teszt</h1>
            <p className="subtitle">Pure Procrastination Scale (PPS)</p>

            <div className="info-box">
                <p><strong>📋 Útmutató:</strong> A Pure Procrastination Scale (PPS) egy 12 tételes, tudományosan validált kérdőív, amely a halogatási hajlamot méri. A kérdőívet több mint 4000 résztvevőn tesztelték világszerte.</p>
                <p><strong>📝 Kitöltés:</strong> Kérjük, értékelje őszintén, mennyire jellemzőek Önre az alábbi állítások. Ne gondolkodjon túl sokat - az első reakciója általában a legpontosabb!</p>
                <p><strong>🎯 Skála:</strong> 1 = Egyáltalán nem jellemző rám | 5 = Nagyon jellemző rám</p>
            </div>

            <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>

            <div className="question-counter">
                <div>
                    {currentQuestionIndex + 1}. kérdés / {shuffledQuestions.length} | Megválaszolva: {answeredCount}
                </div>
                <div className="question-dots">
                    {shuffledQuestions.map((q, index) => (
                        <div
                            key={q.id}
                            className={`question-dot ${index === currentQuestionIndex ? 'current' : ''} ${isQuestionAnswered(q.id) ? 'answered' : ''}`}
                            onClick={() => setCurrentQuestionIndex(index)}
                            title={`Ugrás a ${index + 1}. kérdésre`}
                        />
                    ))}
                </div>
            </div>

            {currentQuestion && (
                <div 
                    className="question-card active"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="question-number">
                        Kérdés {currentQuestionIndex + 1} / {shuffledQuestions.length}
                    </div>
                    <div className="question-text">{currentQuestion.text}</div>
                    <div className="likert-scale">
                        {[1, 2, 3, 4, 5].map(value => (
                            <div key={value} className="likert-option">
                                <input
                                    type="radio"
                                    id={`q${currentQuestion.id}_${value}`}
                                    name={`question${currentQuestion.id}`}
                                    value={value}
                                    checked={answers[currentQuestion.id] === value}
                                    onChange={() => handleAnswerChange(currentQuestion.id, value)}
                                />
                                <label className="likert-label" htmlFor={`q${currentQuestion.id}_${value}`}>
                                    <span className="emoji-indicator">{getEmojiForValue(value)}</span>
                                    {getLabelForValue(value)}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="navigation-buttons">
                <button
                    type="button"
                    className="nav-btn nav-btn-prev"
                    onClick={previousQuestion}
                    disabled={currentQuestionIndex === 0}
                >
                    ← Előző
                </button>
                <button
                    type="button"
                    className="nav-btn nav-btn-next"
                    onClick={nextQuestion}
                    disabled={currentQuestionIndex === shuffledQuestions.length - 1}
                >
                    {currentQuestionIndex === shuffledQuestions.length - 1 ? 'Befejezés ✓' : 'Következő →'}
                </button>
            </div>

            <div className="swipe-hint">
                👈 Húzd jobbra/balra vagy használd a nyilakat 👉
            </div>

            <div className="button-container">
                <button type="button" className="btn btn-evaluate" onClick={evaluateTest}>
                    🎯 Kiértékelés
                </button>
                <button type="button" className="btn btn-pdf" onClick={saveAsPdf} disabled={!showResult}>
                    📄 Mentés PDF-be
                </button>
                <button type="button" className="btn btn-reset" onClick={resetForm}>
                    🔄 Újrakezdés
                </button>
            </div>

            {showResult && result && (
                <div className="result-box show" ref={resultRef}>
                    <h2>📊 Az Ön Eredménye</h2>
                    <div className="score-display">
                        {result.totalScore} / 60 pont (Átlag: {result.averageScore.toFixed(2)})
                    </div>
                    <div
                        className="interpretation"
                        dangerouslySetInnerHTML={{ __html: result.interpretation }}
                    />
                    <div className="warning">
                        <strong>⚠️ Fontos:</strong> Ez a teszt csak tájékoztató jellegű. Ha a halogatás komolyan befolyásolja az életét, munkáját vagy tanulmányait, érdemes lehet pszichológus vagy coach segítségét kérni.
                    </div>
                </div>
            )}

            <p className="author-info">Fejlesztette: Piers Steel (2010) | Validált nemzetközi pszichológiai mérőeszköz</p>
        </div>
        <button
            type="button"
            className="fullscreen-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Kilépés a teljes képernyőből' : 'Teljes képernyő'}
            aria-label={isFullscreen ? 'Kilépés a teljes képernyőből' : 'Teljes képernyő'}
        >
            {isFullscreen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
            ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
            )}
        </button>
        </>
    );
}

export default App;

